import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { synczstyle } from '@/utils/synczstyle';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  private static reloadTimestamp = 0;

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    const errorMessage = error?.message || '';
    const isDynamicImportError = 
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Importing a module script failed') ||
      errorMessage.includes('error loading dynamically imported module');

    // If chunk loading failed (e.g. after server restart or redeployment), auto-reload once cleanly
    if (isDynamicImportError && typeof window !== 'undefined') {
      const now = Date.now();
      if (now - ErrorBoundary.reloadTimestamp > 15000) {
        ErrorBoundary.reloadTimestamp = now;
        console.warn("Dynamic import failure detected. Auto-reloading page with fresh modules...");
        window.location.reload();
      }
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center p-6 text-center animate-in fade-in">
          <div className="max-w-md w-full bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-md shadow-2xl">
            <div className="w-14 h-14 bg-red-500/10 border border-red-500/20 rounded-full flex items-center justify-center text-red-400 mx-auto mb-5">
              <AlertTriangle size={28} />
            </div>
            <h2 className="text-xl font-bold text-white uppercase tracking-wider mb-2">
              Application Refresh Needed
            </h2>
            <p className="text-xs text-neutral-400 leading-relaxed mb-6">
              A module or component was updated in the background. Please refresh to load the latest version.
            </p>
            <button
              onClick={this.handleRetry}
              className={synczstyle('primary', 'w-full py-2.5 px-4 text-xs flex items-center justify-center gap-2 font-bold')}
            >
              <RefreshCw size={14} />
              <span>Reload Application</span>
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
